/**
 * Mentions légales obligatoires d'un devis ou d'une facture : contrôle partagé, appelé avant toute
 * génération de PDF (téléchargement comme envoi). Demande d'Emeni (2026-11-04) : si une information
 * manque, le document ne part pas et un message dit laquelle.
 *
 * L'IBAN n'est volontairement pas contrôlé : il n'a rien à faire sur un devis, et sur une facture il
 * n'est imprimé que pour un paiement par virement (invoicePdfGenerator.ts).
 */

export interface InfosLegalesEtablissement {
    company_name?: string | null;
    address?: string | null; postal_code?: string | null; city?: string | null;
    siret?: string | null; tva?: string | null; tva_regime?: string | null;
    capital?: string | null; rcs_ville?: string | null; rcs_numero?: string | null;
}

export type TypeDocumentLegal = 'devis' | 'facture';

const vide = (v?: string | null) => !String(v ?? '').trim();

/** Liste, en français, des mentions obligatoires qui manquent (tableau vide = document en règle). */
export function mentionsLegalesManquantes(infos: InfosLegalesEtablissement | null | undefined): string[] {
    const i = infos || {};
    const manquantes: string[] = [];

    if (vide(i.company_name)) manquantes.push("le nom de l'entreprise");
    if (vide(i.address) || vide(i.postal_code) || vide(i.city)) manquantes.push("l'adresse complète (rue, code postal, ville)");

    const siret = String(i.siret ?? '').replace(/\s/g, '');
    if (!siret) manquantes.push('le numéro SIRET');
    else if (!/^\d{14}$/.test(siret)) manquantes.push('le numéro SIRET (il doit comporter 14 chiffres)');

    // TVA : soit un numéro de TVA, soit la franchise en base (mention « TVA non applicable, art. 293 B »).
    if (i.tva_regime !== 'franchise_293b' && vide(i.tva)) manquantes.push('le numéro de TVA (ou le régime « franchise en base de TVA »)');

    // Une société (RCS ou capital renseigné) doit afficher les trois ensemble ; une entreprise individuelle n'en a pas.
    const estSociete = !vide(i.rcs_ville) || !vide(i.rcs_numero) || !vide(i.capital);
    if (estSociete) {
        if (vide(i.rcs_ville) || vide(i.rcs_numero)) manquantes.push('la ville et le numéro du RCS');
        if (vide(i.capital)) manquantes.push('le capital social');
    }
    return manquantes;
}

/** Message affiché à l'utilisateur quand le document est bloqué. */
export function messageMentionsManquantes(type: TypeDocumentLegal, manquantes: string[]): string {
    const doc = type === 'devis' ? 'Le devis ne peut pas être téléchargé ni envoyé' : 'La facture ne peut pas être téléchargée ni envoyée';
    return `${doc} : il manque ${manquantes.join(', ')}. Complétez ces informations dans les Paramètres de facturation (Informations légales).`;
}

/** Lève une erreur lisible si une mention obligatoire manque ; sans effet sinon. */
export function exigerMentionsLegales(type: TypeDocumentLegal, infos: InfosLegalesEtablissement | null | undefined): void {
    const manquantes = mentionsLegalesManquantes(infos);
    if (manquantes.length > 0) throw new Error(messageMentionsManquantes(type, manquantes));
}
